import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import {
  FileText,
  Search,
  Star,
  FolderOpen,
  Settings,
  User,
  PenTool,
  Moon,
  Sun,
  GitCompare,
  BookOpen,
  Layers,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTheme } from "next-themes";

interface RecentAnalysis {
  id: string;
  title: string;
  updated_at: string;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  // Fetch recent analyses
  const { data: recentAnalyses = [] } = useQuery({
    queryKey: ['commandPaletteAnalyses'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('analyses')
        .select('id, title, updated_at')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(5);
      
      if (error) throw error;
      return data as RecentAnalysis[];
    },
    staleTime: 30000,
  });

  // Handle keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const runCommand = useCallback((command: () => void) => {
    setOpen(false);
    command();
  }, []);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {/* Quick Actions */}
        <CommandGroup heading="Quick Actions">
          <CommandItem
            onSelect={() => runCommand(() => navigate("/"))}
            className="gap-2"
          >
            <FileText className="h-4 w-4" />
            <span>New Analysis</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => {
              navigate("/");
              // Trigger book search - we'll dispatch a custom event
              window.dispatchEvent(new CustomEvent('openBookSearch'));
            })}
            className="gap-2"
          >
            <Search className="h-4 w-4" />
            <span>Find a Book</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/compare"))}
            className="gap-2"
          >
            <GitCompare className="h-4 w-4" />
            <span>Compare Documents</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/flashcards"))}
            className="gap-2"
          >
            <Layers className="h-4 w-4" />
            <span>Flashcards</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {/* Recent Analyses */}
        {recentAnalyses.length > 0 && (
          <>
            <CommandGroup heading="Recent Analyses">
              {recentAnalyses.map((analysis) => (
                <CommandItem
                  key={analysis.id}
                  onSelect={() => runCommand(() => navigate(`/analysis/${analysis.id}`))}
                  className="gap-2"
                >
                  <BookOpen className="h-4 w-4" />
                  <span className="truncate">{analysis.title}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Navigation */}
        <CommandGroup heading="Navigation">
          <CommandItem
            onSelect={() => runCommand(() => navigate("/analyses"))}
            className="gap-2"
          >
            <FolderOpen className="h-4 w-4" />
            <span>All Analyses</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/favorites"))}
            className="gap-2"
          >
            <Star className="h-4 w-4" />
            <span>Favorites</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/chats"))}
            className="gap-2"
          >
            <PenTool className="h-4 w-4" />
            <span>Writing Chats</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {/* Settings */}
        <CommandGroup heading="Settings">
          <CommandItem
            onSelect={() => runCommand(() => setTheme(theme === 'dark' ? 'light' : 'dark'))}
            className="gap-2"
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
            <span>Toggle Theme</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/profile"))}
            className="gap-2"
          >
            <User className="h-4 w-4" />
            <span>Profile</span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate("/settings"))}
            className="gap-2"
          >
            <Settings className="h-4 w-4" />
            <span>Settings</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
